import { useMemo } from 'react';
import { useFindByQuery } from '../../graphql';
import { Message } from '../domain';
import { ISSUE_MESSAGE_FRAGMENT } from './issueMessageFragment';

/**
 * Side Chat (plan 0022 §C): resolve a single IssueMessage by id.
 *
 * The Side Chat panel is rendered from a URL param (`/sidechat/:messageId`), so
 * the origin message object isn't carried through navigation — it must be
 * re-fetched from its id. `findIssueMessages(filter: { id: { eq } })` returns at
 * most one row; we select the same fragment the thread uses (plus `promptRoutine`
 * so a routine-typed origin renders identically) and hand back the single item.
 *
 * `enabled` is gated on a truthy id — the panel briefly mounts with an empty id
 * while a column slides out, and an unguarded query would send `{ id: "" }`.
 */
export const useGetIssueMessage = (messageId?: string) => {
  const { data, isLoading, refetch, invalidateQuery } = useFindByQuery({
    modelName: 'issueMessage',
    query: GET_ISSUE_MESSAGE,
    variables: {
      filter: { id: { eq: messageId } },
      page: { limit: 1, offset: 0 },
    },
    options: { enabled: !!messageId },
  });

  const message = useMemo(
    () => (data?.items?.[0] as Message | undefined) ?? undefined,
    [data]
  );

  return { message, isLoading, refetch, invalidateQuery };
};

const GET_ISSUE_MESSAGE = `
  query findIssueMessages($filter: IssueMessageFilter, $page: PageRequest) {
    findIssueMessages(filter: $filter, page: $page) {
      items {
        ${ISSUE_MESSAGE_FRAGMENT}
        # Selected here rather than in ISSUE_MESSAGE_FRAGMENT: that fragment is
        # shared with the anonymous share-link reply, where anything beyond a
        # bare id on a relationship trips the permission proxy. A side-chat
        # route carries only a messageId, so this is how its caller finds the
        # activity to load.
        issue {
          id
        }
        promptRoutine {
          id
          name
          currentVersion {
            id
          }
          type
          config
        }
      }
      count
      offset
      limit
    }
  }
`;
